/** @jsxImportSource @emotion/react */
import React, { Component, useState, useCallback, useEffect, useMemo } from 'react';
import { css, CSSObject, jsx } from '@emotion/react';
import * as yup from 'yup';
import Spinner from '@atlaskit/spinner';
import {
  Form,
  arrayMutators,
  validate, createDecorator,
} from 'components/form';
import { useParams } from 'react-router';
import useCompanyNavigation from '../auth/useCompanyNavigation';
import { queryCache, useMutation, useQuery } from 'react-query';
import { FormattedMessage } from 'components/i18n';
import SaleEditSplitPane from './components/SaleEditSplitPane';
import SaleForm from './SaleForm';
import { RecurringSale, Sale, VatCode } from './types';
import { PageWrapper } from '../../components/page';
import { useNotifications } from '../../components/notification';
import { getRecurringSaleById, getSaleById } from './SalePage';
import { saveRecurringSale, saveSale } from './apiActions';
import useSaleHelpers from './hooks/useSaleHelpers';
import RecurringSaleEditHeader from './components/RecurringSaleEditHeader';
import SaleEditFooter from './components/SaleEditFooter';
import { useCurrentCompany } from '../auth/CurrentCompany';
import RecurringSaleEditFooter from './components/RecurringSaleEditFooter';
import { deleteRecurringSale, saleLinesSchema } from "./SaleEditPage";
import { DateTime } from 'luxon';
import { Decorator } from "final-form";
import { useConfirm } from "../../components/modal-dialog";

const defaultProps = {
  isNew: false,
};

type Props = {
} & typeof defaultProps;

const schema = yup.object({
    intervalNumber: yup.number().required('Required'),
    intervalUnit: yup.string().required('Required'),
    customer: yup.mixed().required('Required'),
    startDate: yup.string().required('Required'),
    endDate: yup.string().nullable(),
    description: yup.string(),
    Lines: saleLinesSchema,
    note: yup.string(),
    footer: yup.string(),
});

const paymentTermDecorator = createDecorator({
  field: /customer/, // when invoiceDate or customer changes...
  updates: (fieldValue: any, name, allValues: Sale | undefined, prevValues: Sale | undefined) => {
    if(!prevValues?.paymentTerm) return {}; // fix to skip on initialValues
    return {
      paymentTerm: fieldValue?.paymentTerm || 14,
    }
  }
});

const RecurringSaleEditPage = ({isNew}: Props) => {

  const navigate = useCompanyNavigation();
  const currentCompany = useCurrentCompany();
  const [recurringSale, setRecurringSale] = useState<Partial<RecurringSale>|null>(null);
  const { id } = useParams();
  const { data: saleById } = useQuery(['recurringSale', id], getRecurringSaleById, { enabled: !isNew && id });
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const saleHelpers = useSaleHelpers();
  const { confirmDelete } = useConfirm();

  if(!currentCompany?.invoiceSettings) {
    navigate('/sale/invoice-settings')
  }

  const getNewSale = useCallback(async(defaultVatCode: VatCode) => {
    const invoiceSettings = currentCompany?.invoiceSettings || {};
    const newRecurringSale = {
      isNew: true,
      status: 'inactive',
      startDate: new Date().toISOString(),
      Lines: [{
        description: '',
        vatCode: defaultVatCode,
        vatCodeId: defaultVatCode?.id,
        item: null,
        itemId: null,
        price: null,
        quantity: 1,
        amount: null,
      }],
      intervalNumber: 1,
      intervalUnit: 'month',
      description: invoiceSettings.defaultDescription,
      note: invoiceSettings.defaultNote,
      footer: invoiceSettings.defaultFooter,
    } as Partial<RecurringSale>;
    return newRecurringSale;
  },[currentCompany?.invoiceSettings]);

  useEffect( () => {
    (async () => {
      if(!saleHelpers.vatCodes) return;
      if(!saleHelpers.getDefaultVatCode) return;
      if(recurringSale) return;
      if(saleById) {
        setRecurringSale(saleById);
      }
      if(isNew) {
        const defaultVatCode = saleHelpers.getDefaultVatCode({});
        const newRecurringSale = await getNewSale(defaultVatCode);
        setRecurringSale(newRecurringSale);
      }
    })()
  },[saleById, isNew, saleHelpers]);

  const [saveRecurring, { isLoading: isRecurringSaving }] = useMutation(saveRecurringSale, {
    onSuccess: (savedSale: Sale) => {
      queryCache.invalidateQueries('recurringSales');
      queryCache.invalidateQueries(['recurringSale', savedSale.id]);
      showSuccessNotification(
        <FormattedMessage
          id="sale.RecurringSaleEditPage.notifications.saveRecurring.success.title"
          defaultMessage="Recurring invoice saved"
        />);
      navigate(`/sale/recurring-invoices`);
    },
    onError: (e: any) => {
      showErrorNotification(
        e.message
      )}
  });
  const {decorators} = saleHelpers;

  const allDecorators: Decorator[] = useMemo(()=>{
    if(decorators) {
      return decorators;
    }
    return []

  },[decorators])

  const handleDelete = useCallback(async () => {
    confirmDelete({
      title: (
        <FormattedMessage
          id="sale.RecurringSaleEditPage.notifications.delete.title"
          defaultMessage="Delete this recurring invoice?"
        />
      ),
      content: (
        <FormattedMessage
          id="sale.RecurringSaleEditPage.notifications.delete.content"
          defaultMessage="You are about to delete this recurring invoice."
        />
      ),
      onConfirm: async () => {
        try {
          await deleteRecurringSale(recurringSale.id);
          showSuccessNotification(
            <FormattedMessage
              id="sale.RecurringSaleEditPage.notifications.delete.success.title"
              defaultMessage="Recurring invoice deleted"
            />
          );
        } catch (e) {
          showErrorNotification(e.message);
        }
        queryCache.invalidateQueries('recurringSales');
        // navigate('../')
        navigate(-1) // FIXME change to ../ and prevent bounce back from /sales
      },
    });
  }, [recurringSale?.id]);

  const isLoading = !recurringSale || !decorators;

  if (!recurringSale || isLoading) {
    return <SaleEditSplitPane isLoading

    > <Spinner/>
    </SaleEditSplitPane>
  }

  return (
    <Form<any>
      initialValues={recurringSale || {}}
      onSubmit={async (values) => {
            await saveRecurring(values);
        }
      }
      validate={validate(schema)}
      decorators={allDecorators}
      // @ts-ignore
      mutators={arrayMutators}
      subscription={{ submitting: true, pristine: true }}
    >
      {(formProps) => {
        return (
          <SaleEditSplitPane>
            {!recurringSale || isLoading?  <Spinner/>  :
              <PageWrapper fullWidth>
                <RecurringSaleEditHeader isNew={isNew} recurringSale={recurringSale}/>
                <SaleForm isNew={isNew} isRecurring {...formProps} isSaving={isRecurringSaving} getDefaultVatCode={saleHelpers.getDefaultVatCode} />
                <RecurringSaleEditFooter isNew={isNew} isActive={recurringSale.isActive} lastInvoiceDate={recurringSale.lastInvoiceDate}  isSaving={isRecurringSaving} onRemove={handleDelete} {...formProps}/>
              </PageWrapper>}
          </SaleEditSplitPane>
        );
      }}
    </Form>
  );
};

RecurringSaleEditPage.defaultProps = defaultProps;

export default RecurringSaleEditPage;
